// Coding Interactive Component - CODING ONLY DESPITE STANDARD NAME FOR FILE

import React, { useState, useEffect } from 'react';
import { PlayIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface InteractiveExerciseProps {
  exerciseData: {
    instructions: string;
    initialCode: string;
    expectedOutput?: string;
    hints?: string[];
  };
  onInteraction: (interaction: string) => void;
  onGradingUpdate?: (grading: GradingData) => void;
  isModuleComplete?: boolean;
}

interface GradingData {
  attempts: number;
  incorrectAttempts: number;
  hintsUsed: number;
  completionTime: number;
  score: number;
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  feedback: string[];
}

const InteractiveExercise: React.FC<InteractiveExerciseProps> = ({
  exerciseData,
  onInteraction,
  onGradingUpdate,
  isModuleComplete = false
}) => {
  const [code, setCode] = useState(exerciseData.initialCode);
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [currentHintIndex, setCurrentHintIndex] = useState(0);
  
  // Grading state
  const [attempts, setAttempts] = useState(0);
  const [incorrectAttempts, setIncorrectAttempts] = useState(0);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [startTime] = useState(Date.now());
  const [isCompleted, setIsCompleted] = useState(false);
  const [showGrading, setShowGrading] = useState(false);
  const [windowFocused, setWindowFocused] = useState(true);
  const [tabSwitches, setTabSwitches] = useState(0);
  const [suspiciousActivity, setSuspiciousActivity] = useState(false);

  // Window focus tracking for anti-cheating
  useEffect(() => {
    if (isCompleted) return;

    let focusChangeTimeout: NodeJS.Timeout | null = null;

    const handleFocus = () => {
      setWindowFocused(true);
      // Clear any pending tab switch increment
      if (focusChangeTimeout) {
        clearTimeout(focusChangeTimeout);
        focusChangeTimeout = null;
      }
    };

    const handleBlur = () => {
      setWindowFocused(false);
      // Use a small delay to avoid double counting with visibility change
      focusChangeTimeout = setTimeout(() => {
        setTabSwitches(prev => {
          const newCount = prev + 1;
          if (newCount >= 3) {
            setSuspiciousActivity(true);
            toast.error('⚠️ Multiple tab switches detected. This may affect your exercise submission.');
          }
          return newCount;
        });
        focusChangeTimeout = null;
      }, 100);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setWindowFocused(false);
        // Only count if not already counted by blur event
        if (!focusChangeTimeout) {
          setTabSwitches(prev => {
            const newCount = prev + 1;
            if (newCount >= 3) {
              setSuspiciousActivity(true);
              toast.error('⚠️ Multiple tab switches detected. This may affect your exercise submission.');
            }
            return newCount;
          });
        }
      } else {
        setWindowFocused(true);
        // Clear any pending tab switch increment
        if (focusChangeTimeout) {
          clearTimeout(focusChangeTimeout);
          focusChangeTimeout = null;
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (focusChangeTimeout) {
        clearTimeout(focusChangeTimeout);
      }
    };
  }, [isCompleted]);

  const validateJSXStructure = (code: string): { isValid: boolean; error?: string } => {
    try {
      // Check for basic React component structure first
      const hasFunctionDeclaration = /function\s+[A-Z][a-zA-Z0-9]*/.test(code);
      if (!hasFunctionDeclaration) {
        return { isValid: false, error: 'Component must start with a capitalized function name (e.g., function Welcome)' };
      }
      
      const hasReturn = /return\s*\(?\s*</.test(code);
      if (!hasReturn) {
        return { isValid: false, error: 'Component must return JSX (e.g., return <div>...</div>)' };
      }
      
      // Check for balanced JSX tags
      const jsxTagRegex = /<\/?[a-zA-Z][a-zA-Z0-9]*(?:\s[^>]*)?>|<[a-zA-Z][a-zA-Z0-9]*(?:\s[^>]*)?\/>/g;
      const tags = code.match(jsxTagRegex) || [];
      const stack: string[] = [];
      
      for (const tag of tags) {
        if (tag.endsWith('/>')) {
          // Self-closing tag, no need to track
          continue;
        }
        
        if (tag.startsWith('</')) {
          // Closing tag
          const tagName = tag.match(/<\/([a-zA-Z][a-zA-Z0-9]*)/)?.[1];
          if (!tagName) continue;
          
          const lastTag = stack.pop();
          if (lastTag !== tagName) {
            return { isValid: false, error: `Mismatched JSX tags: expected closing tag for <${lastTag}> but found </${tagName}>` };
          }
        } else {
          // Opening tag
          const tagName = tag.match(/<([a-zA-Z][a-zA-Z0-9]*)/)?.[1];
          if (tagName) {
            stack.push(tagName);
          }
        }
      }
      
      // Check if all tags are closed
      if (stack.length > 0) {
        return { isValid: false, error: `Unclosed JSX tag: <${stack[stack.length - 1]}>` };
      }
      
      return { isValid: true };
    } catch (error) {
      return { isValid: false, error: 'Invalid JSX syntax' };
    }
  };

  const calculateGrading = (): GradingData => {
    const completionTime = Math.round((Date.now() - startTime) / 1000); // in seconds
    let score = 100;
    const feedback: string[] = [];

    // Deduct points for incorrect attempts
    if (incorrectAttempts > 0) {
      const deduction = Math.min(incorrectAttempts * 10, 40);
      score -= deduction;
      feedback.push(`-${deduction} points for ${incorrectAttempts} incorrect attempt(s)`);
    }

    // Deduct points for using hints
    if (hintsUsed > 0) {
      const deduction = Math.min(hintsUsed * 5, 20);
      score -= deduction;
      feedback.push(`-${deduction} points for using ${hintsUsed} hint(s)`);
    }

    // Deduct points for suspicious activity
    if (suspiciousActivity || tabSwitches > 3) {
      const deduction = Math.min(tabSwitches * 2, 10);
      score -= deduction;
      feedback.push(`-${deduction} points for tab switching/loss of focus`);
    }

    // Time bonus/penalty
    if (completionTime < 60) {
      score += 5;
      feedback.push('+5 points for quick completion');
    } else if (completionTime > 300) {
      score -= 5;
      feedback.push('-5 points for taking more than 5 minutes');
    }

    // Ensure score is within bounds
    score = Math.max(0, Math.min(100, score));

    // Calculate letter grade
    let grade: 'A' | 'B' | 'C' | 'D' | 'F';
    if (score >= 90) grade = 'A';
    else if (score >= 80) grade = 'B';
    else if (score >= 70) grade = 'C';
    else if (score >= 60) grade = 'D';
    else grade = 'F';

    return {
      attempts,
      incorrectAttempts,
      hintsUsed,
      completionTime,
      score,
      grade,
      feedback
    };
  };

  const runCode = () => {
    setIsRunning(true);
    setAttempts(prev => prev + 1);
    onInteraction('code_execution_started');
    
    // Actually execute the user's code
    setTimeout(() => {
      try {
        let result = '';
        
        // Safety check for code
        if (!code || typeof code !== 'string') {
          setOutput('No code to execute');
          setIsRunning(false);
          return;
        }
        
        // Create a console capture for console.log statements
        const consoleOutput: string[] = [];
        const originalLog = console.log;
        
        // Override console.log temporarily
        console.log = (...args: any[]) => {
          consoleOutput.push(args.map(arg => 
            typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
          ).join(' '));
        };

        // For React/JSX components, evaluate them properly
        if (code.includes('function') && code.includes('return') && code.includes('<')) {
          try {
            // Validate function exists
            
            // Execute JSX component properly
            const executeJSXComponent = (code: string): string => {
              try {
                // Extract function signature to get default values
                const funcSignatureMatch = code.match(/function\s+(\w+)\s*\(\s*\{\s*([^}]*)\s*\}\s*\)/);
                if (!funcSignatureMatch) return 'Invalid function signature';
                
                const functionName = funcSignatureMatch[1];
                const paramsString = funcSignatureMatch[2];
                
                // Parse default values from destructured parameters
                const defaultProps: Record<string, any> = {};
                
                // Handle parameters like "name = 'Ron'" or "name='Ron'"
                const paramMatches = paramsString.match(/(\w+)\s*=\s*['"]([^'"]*)['"]/g);
                if (paramMatches) {
                  paramMatches.forEach(match => {
                    const matchResult = match.match(/(\w+)\s*=\s*['"]([^'"]*)['"]/);
                    if (matchResult) {
                      const [, paramName, defaultValue] = matchResult;
                      defaultProps[paramName] = defaultValue;
                    }
                  });
                }
                
                // Create function that properly handles destructuring with defaults
                const func = new Function(`
                  ${code}
                  return ${functionName};
                `)();
                
                // Call component with empty props to use defaults
                const componentResult = func({});
                
                // Convert result to readable text
                return componentResult.replace(/<[^>]*>/g, '').trim();
              } catch (error) {
                // Enhanced fallback with better default value handling
                const returnMatch = code.match(/return\s+([^;]+);?/);
                if (!returnMatch) return 'No JSX return found';
                
                let jsx = returnMatch[1].trim();
                if (jsx.startsWith('(') && jsx.endsWith(')')) {
                  jsx = jsx.slice(1, -1).trim();
                }
                
                // Extract default values from function signature
                const funcSignatureMatch = code.match(/function\s+\w+\s*\(\s*\{\s*([^}]*)\s*\}\s*\)/);
                let defaultValue = 'Guest';
                
                if (funcSignatureMatch) {
                  const paramsString = funcSignatureMatch[1];
                  const defaultMatch = paramsString.match(/name\s*=\s*['"]([^'"]*)['"]/);
                  if (defaultMatch) {
                    defaultValue = defaultMatch[1];
                  }
                }
                
                // Handle JSX expressions with proper default resolution
                jsx = jsx.replace(/\{([^}]+)\}/g, (_, expr) => {
                  if (expr.trim() === 'name') {
                    return defaultValue;
                  }
                  
                  if (expr.includes('||')) {
                    const parts = expr.split('||').map((p: string) => p.trim());
                    if (parts[0] === 'name' && parts[1].match(/['"]([^'"]+)['"]/)) {
                      return parts[1].replace(/['"]/g, '');
                    }
                  }
                  
                  return expr;
                });
                
                // Remove HTML tags to get text content
                return jsx.replace(/<[^>]*>/g, '').trim();
              }
            };
            
            result = executeJSXComponent(code);
          } catch (jsxError) {
            result = `JSX Error: ${(jsxError as Error).message}`;
          }
        }
        // Handle console.log statements by actually executing the code
        else if (code.includes('console.log')) {
          try {
            eval(code);
            result = consoleOutput.join('\n') || 'console.log executed';
          } catch (logError) {
            result = `Error: ${(logError as Error).message}`;
          }
        }
        // Handle mathematical expressions and other JavaScript
        else {
          try {
            const executeResult = eval(code);
            if (executeResult !== undefined) {
              result = String(executeResult);
            } else if (consoleOutput.length > 0) {
              result = consoleOutput.join('\n');
            } else {
              result = 'Code executed successfully';
            }
          } catch (execError) {
            result = `Error: ${(execError as Error).message}`;
          }
        }
        
        // Restore original console.log
        console.log = originalLog;
        
        setOutput(result || 'No output');
        onInteraction('code_executed_successfully');
        
        // Validate JSX components and check expected output
        if (exerciseData.expectedOutput) {
          const isCorrect = (result || 'No output').trim() === exerciseData.expectedOutput.trim();
          
          // Additional JSX validation for React components
          if (code.includes('function') && code.includes('return') && code.includes('<')) {
            // Validate JSX syntax and structure
            const jsxValidation = validateJSXStructure(code);
            if (!jsxValidation.isValid) {
              setOutput(result + `\n\nJSX Validation Error: ${jsxValidation.error}`);
              setIncorrectAttempts(prev => prev + 1);
              onInteraction('jsx_validation_failed');
              setIsRunning(false);
              return;
            }
          }
          
          if (isCorrect) {
            setIsCompleted(true);
            const gradingData = calculateGrading();
            onInteraction('solution_correct');
            onGradingUpdate?.(gradingData);
            if (isModuleComplete) {
              setShowGrading(true);
            }
          } else {
            setIncorrectAttempts(prev => prev + 1);
            onInteraction('solution_incorrect');
          }
        } else {
          // If no expected output, assume execution is completion
          setIsCompleted(true);
          const gradingData = calculateGrading();
          onInteraction('solution_correct');
          onGradingUpdate?.(gradingData);
          if (isModuleComplete) {
            setShowGrading(true);
          }
        }
      } catch (error: any) {
        setOutput(`Error: ${error.message}`);
        onInteraction('code_execution_error');
      }
      setIsRunning(false);
    }, 500);
  };

  const resetCode = () => {
    setCode(exerciseData.initialCode);
    setOutput('');
    onInteraction('code_reset');
  };


  const showNextHint = () => {
    if (exerciseData.hints && currentHintIndex < exerciseData.hints.length - 1) {
      setCurrentHintIndex(prev => prev + 1);
      setHintsUsed(prev => prev + 1);
      onInteraction(`hint_${currentHintIndex + 1}_viewed`);
    }
  };

  const toggleHints = () => {
    setShowHints(!showHints);
    if (!showHints && hintsUsed === 0) {
      setHintsUsed(1);
    }
  };

  const isOutputCorrect = exerciseData.expectedOutput ? output.trim() === exerciseData.expectedOutput.trim() : null;

  return (
    <div className="bg-gray-50 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Interactive Exercise</h3>
        <div className="flex items-center space-x-3">
          {/* Focus indicator */}
          <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
            windowFocused ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            <div className={`w-2 h-2 rounded-full mr-1 ${
              windowFocused ? 'bg-green-500' : 'bg-red-500'
            }`}></div>
            {windowFocused ? 'Focused' : 'Not Focused'}
          </div>
          
          {/* Tab switches warning */}
          {tabSwitches > 0 && (
            <div className="flex items-center text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-800">
              ⚠️ {tabSwitches} tab switch{tabSwitches !== 1 ? 'es' : ''}
            </div>
          )}
        </div>
      </div>
      <p className="text-gray-600 mb-6">{exerciseData.instructions}</p>

      {/* Code Editor */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Code Editor
        </label>
        <textarea
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="w-full h-40 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-900 text-green-400"
          placeholder="Write your code here..."
          spellCheck={false}
        />
      </div>

      {/* Control Buttons */}
      <div className="flex flex-wrap gap-3 mb-4">
        <button
          onClick={runCode}
          disabled={isRunning}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors"
        >
          <PlayIcon className="h-4 w-4 mr-2" />
          {isRunning ? 'Running...' : 'Run Code'}
        </button>
        
        <button
          onClick={resetCode}
          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          Reset
        </button>

        {exerciseData.hints && exerciseData.hints.length > 0 && (
          <button
            onClick={toggleHints}
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
          >
            💡 {showHints ? 'Hide Hints' : 'Show Hints'}
          </button>
        )}
      </div>

      {/* Hints Section */}
      {showHints && exerciseData.hints && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <h4 className="font-medium text-yellow-900 mb-2">Hints:</h4>
          {exerciseData.hints.slice(0, currentHintIndex + 1).map((hint, index) => (
            <div key={index} className="text-sm text-yellow-800 mb-2">
              <span className="font-medium">Hint {index + 1}:</span> {hint}
            </div>
          ))}
          {currentHintIndex < exerciseData.hints.length - 1 && (
            <button
              onClick={showNextHint}
              className="text-sm text-yellow-700 hover:text-yellow-900 underline"
            >
              Show next hint
            </button>
          )}
        </div>
      )}

      {/* Output Section */}
      {output && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Output
          </label>
          <div className="bg-gray-900 text-green-400 p-4 rounded-md font-mono text-sm whitespace-pre-wrap border">
            {output}
          </div>
          
          {/* Solution Check */}
          {exerciseData.expectedOutput && (
            <div className="mt-2 flex items-center">
              {isOutputCorrect === true && (
                <div className="flex items-center text-green-600">
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">Correct! Well done!</span>
                </div>
              )}
              {isOutputCorrect === false && (
                <div className="flex items-center text-red-600">
                  <XCircleIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">
                    Not quite right. Expected: "{exerciseData.expectedOutput}"
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Expected Output (for reference) */}
      {exerciseData.expectedOutput && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <h4 className="text-sm font-medium text-blue-900 mb-1">Expected Output:</h4>
          <code className="text-sm text-blue-800">{exerciseData.expectedOutput}</code>
        </div>
      )}

      {/* Progress Indicator */}
      {!isCompleted && attempts > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-600">
            <span>Attempts: {attempts}</span>
            <span>Incorrect: {incorrectAttempts}</span>
            <span>Hints Used: {hintsUsed}</span>
            <span>Tab Switches: {tabSwitches}</span>
          </div>
          {suspiciousActivity && (
            <div className="mt-2 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded px-2 py-1">
              ⚠️ Suspicious activity detected - this may affect your score
            </div>
          )}
        </div>
      )}

      {/* Grading Section */}
      {showGrading && isCompleted && (
        <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6 mt-4">
          <div className="text-center mb-4">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Exercise Complete!</h3>
            <div className="flex items-center justify-center space-x-4">
              <div className="text-4xl font-bold text-green-600">
                {calculateGrading().grade}
              </div>
              <div className="text-2xl font-semibold text-gray-700">
                {calculateGrading().score}/100
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="bg-white rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Performance Metrics</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Total Attempts:</span>
                  <span className="font-medium">{attempts}</span>
                </div>
                <div className="flex justify-between">
                  <span>Incorrect Attempts:</span>
                  <span className="font-medium text-red-600">{incorrectAttempts}</span>
                </div>
                <div className="flex justify-between">
                  <span>Hints Used:</span>
                  <span className="font-medium text-yellow-600">{hintsUsed}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tab Switches:</span>
                  <span className={`font-medium ${tabSwitches > 3 ? 'text-orange-600' : 'text-gray-600'}`}>{tabSwitches}</span>
                </div>
                <div className="flex justify-between">
                  <span>Completion Time:</span>
                  <span className="font-medium">{Math.floor(calculateGrading().completionTime / 60)}m {calculateGrading().completionTime % 60}s</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Score Breakdown</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Base Score:</span>
                  <span className="font-medium text-green-600">100</span>
                </div>
                {calculateGrading().feedback.map((item, index) => (
                  <div key={index} className="flex justify-between text-xs">
                    <span className="flex-1">{item}</span>
                  </div>
                ))}
                <div className="border-t pt-2 mt-2 flex justify-between font-semibold">
                  <span>Final Score:</span>
                  <span className="text-green-600">{calculateGrading().score}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <div className="text-lg font-semibold text-gray-800 mb-2">
              {calculateGrading().grade === 'A' && '🌟 Excellent work!'}
              {calculateGrading().grade === 'B' && '👍 Good job!'}
              {calculateGrading().grade === 'C' && '✅ Well done!'}
              {calculateGrading().grade === 'D' && '📚 Keep practicing!'}
              {calculateGrading().grade === 'F' && '💪 Don\'t give up!'}
            </div>
            <button
              onClick={() => setShowGrading(false)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Continue Learning
            </button>
          </div>
        </div>
      )}

      {/* Quick Grade Preview for Completed Exercises */}
      {isCompleted && !showGrading && isModuleComplete && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-green-800">Exercise Completed</span>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-lg font-bold text-green-600">{calculateGrading().grade}</span>
              <button
                onClick={() => setShowGrading(true)}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
              >
                View Grade
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveExercise;